import React from 'react';
import { CheckCircle, AlertTriangle, TrendingDown, TrendingUp, Award } from 'lucide-react';
import type { SummaryAssessment, Verdict } from '../../../types/acquisitionAnalysis';

interface SummaryAssessmentSectionProps {
  data: SummaryAssessment;
}

export const SummaryAssessmentSection: React.FC<SummaryAssessmentSectionProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'Positive':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'Negative':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default:
        return <TrendingUp className="w-4 h-4 text-blue-400" />;
    }
  };

  const getImpactBadge = (impact: string) => {
    const configs: Record<string, string> = {
      Positive: 'bg-green-500/20 text-green-300 border-green-500/30',
      Negative: 'bg-red-500/20 text-red-300 border-red-500/30',
      Neutral: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          configs[impact] || configs.Neutral
        }`}
      >
        {getImpactIcon(impact)}
        {impact}
      </span>
    );
  };

  const getVerdictDisplay = (verdict: Verdict) => {
    const configs: Record<
      Verdict,
      {
        label: string;
        icon: typeof CheckCircle;
        className: string;
        description: string;
      }
    > = {
      STRONG_BUY: {
        label: 'Strong Buy',
        icon: CheckCircle,
        className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        description:
          'Exceptional opportunity with strong growth, low risk, and favorable valuation metrics.',
      },
      BUY: {
        label: 'Buy',
        icon: TrendingUp,
        className: 'bg-green-500/20 text-green-300 border-green-500/30',
        description:
          'Solid acquisition target with positive fundamentals and reasonable valuation.',
      },
      CAUTION: {
        label: 'Caution',
        icon: AlertTriangle,
        className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
        description:
          'Proceed with caution. Some risk factors present that warrant careful consideration.',
      },
      AVOID: {
        label: 'Avoid',
        icon: TrendingDown,
        className: 'bg-red-500/20 text-red-300 border-red-500/30',
        description: 'Not recommended. Significant risk factors outweigh potential benefits.',
      },
    };

    const config = configs[verdict];
    const Icon = config.icon;

    return (
      <div className={`p-6 rounded-lg border ${config.className}`}>
        <div className="flex items-center gap-3 mb-3">
          <Icon className="w-8 h-8" />
          <div>
            <h3 className="text-2xl font-bold text-white">{config.label}</h3>
            <p className="text-sm opacity-90">{config.description}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overall Verdict */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-indigo-400" />
          Overall Assessment
        </h3>
        {getVerdictDisplay(data.verdict)}
      </div>

      {/* Key Factors */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Key Factors</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Factor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Impact
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.factors.map((factor, index) => (
                <tr key={index} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-white">{factor.factor}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{factor.status}</td>
                  <td className="px-4 py-3 text-center">{getImpactBadge(factor.impact)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Valuation Range */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Valuation Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-700/30 border border-slate-600 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-400">Conservative ({data.valuationRange.conservative.multiple}x)</span>
              <span className="text-xl font-bold text-white">
                {formatCurrency(data.valuationRange.conservative.value)}
              </span>
            </div>
          </div>

          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-blue-300">Mid-Range ({data.valuationRange.midRange.multiple}x)</span>
              <span className="text-xl font-bold text-blue-300">
                {formatCurrency(data.valuationRange.midRange.value)}
              </span>
            </div>
          </div>

          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-purple-300">Premium ({data.valuationRange.premium.multiple}x)</span>
              <span className="text-xl font-bold text-purple-300">
                {formatCurrency(data.valuationRange.premium.value)}
              </span>
            </div>
          </div>

          {data.valuationRange.forecastBased && (
            <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-indigo-300">
                  Forecast-Based ({data.valuationRange.forecastBased.multiple}x)
                </span>
                <span className="text-xl font-bold text-indigo-300">
                  {formatCurrency(data.valuationRange.forecastBased.value)}
                </span>
              </div>
              <p className="text-xs text-indigo-400">Based on projected FY performance</p>
            </div>
          )}
        </div>
      </div>

      {/* Final Recommendation */}
      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
        <h4 className="text-sm font-semibold text-indigo-300 mb-2">Recommendation</h4>
        <p className="text-sm text-indigo-300">
          {data.verdict === 'STRONG_BUY' &&
            'This is an exceptional acquisition opportunity. Strong fundamentals across all key metrics. Recommend proceeding with offer in the premium valuation range.'}
          {data.verdict === 'BUY' &&
            'This is a solid acquisition target. Most metrics are favorable. Recommend proceeding with offer in the mid-range valuation.'}
          {data.verdict === 'CAUTION' &&
            'Proceed with careful consideration. While some positive factors exist, there are notable risks that should be addressed in due diligence. Consider conservative valuation or negotiate key retention agreements.'}
          {data.verdict === 'AVOID' &&
            'Not recommended at this time. Significant risk factors outweigh potential benefits. Consider alternative targets or wait for improved fundamentals.'}
        </p>
      </div>
    </div>
  );
};
